/**
 * Adapters between Solara Core DTOs (@solara/api-client) and the
 * admin-console mock shapes used by the existing UI. We keep these
 * minimal — only InvoiceDto/TenantDto get mapped here. Other resources
 * render against the DTOs directly in the page bodies.
 */
import type { InvoiceDto, TenantDto } from '@solara/api-client';

export function minorToAed(minor: string | number | null | undefined): number {
  if (minor == null) return 0;
  const n = typeof minor === 'string' ? Number(minor) : minor;
  if (!Number.isFinite(n)) return 0;
  return n / 100;
}

/**
 * Lightweight row shape used by the admin-console InvoicesPage / ArchivePage /
 * DashboardPage. Mirrors the legacy `SampleInvoiceRow` field set the existing
 * UI already consumes.
 */
export interface AdminInvoiceRow {
  invoiceId: string;
  tenantId: string;
  invoiceNumber: string;
  direction: InvoiceDto['direction'];
  status: InvoiceDto['status'];
  mlsStatus: 'AB' | 'AP' | 'RE' | 'PENDING';
  buyerName: string;
  buyerTrn: string;
  sellerName: string;
  sellerTrn: string;
  issueDate: string;
  totalNetAmount: number;
  totalVatAmount: number;
  totalGrossAmount: number;
  acknowledgedAt?: string | null;
  archivedAt?: string | null;
}

function statusToMls(inv: InvoiceDto): AdminInvoiceRow['mlsStatus'] {
  if (inv.mlsState === 'AB') return 'AB';
  if (inv.mlsState === 'AP') return 'AP';
  if (inv.mlsState === 'RE') return 'RE';
  if (inv.status === 'ACKNOWLEDGED' || inv.status === 'ARCHIVED') return 'AP';
  if (
    inv.status === 'DELIVERED' ||
    inv.status === 'SENDING' ||
    inv.status === 'SIGNED'
  )
    return 'AB';
  if (inv.status === 'REJECTED' || inv.status === 'FAILED') return 'RE';
  return 'PENDING';
}

export function adaptInvoiceRow(inv: InvoiceDto): AdminInvoiceRow {
  return {
    invoiceId: inv.id,
    tenantId: inv.tenantId,
    invoiceNumber: inv.invoiceNumber,
    direction: inv.direction,
    status: inv.status,
    mlsStatus: statusToMls(inv),
    buyerName: inv.buyerName,
    buyerTrn: inv.buyerTrn,
    sellerName: inv.sellerName,
    sellerTrn: inv.sellerTrn,
    issueDate: inv.issueDate,
    totalNetAmount: minorToAed(inv.subtotalMinor),
    totalVatAmount: minorToAed(inv.vatMinor),
    totalGrossAmount: minorToAed(inv.totalMinor),
    acknowledgedAt: inv.acknowledgedAt ?? null,
    archivedAt: inv.archivedAt ?? null,
  };
}

export function adaptInvoiceRows(
  list: InvoiceDto[] | undefined,
): AdminInvoiceRow[] {
  return (list ?? []).map(adaptInvoiceRow);
}

export interface AdminTenantRow {
  tenantId: string;
  companyNameEn: string;
  companyNameAr: string;
  trn: string;
  emirate: string;
  industry: string;
  onboardingStatus: 'ACTIVE' | 'SUSPENDED' | 'TERMINATED' | 'PENDING';
  peppolId: string;
  contactEmail: string;
  contactPhone: string;
  invoicesYtd: number;
  usersCount: number;
  createdAt: string;
}

export function adaptTenant(t: TenantDto): AdminTenantRow {
  return {
    tenantId: t.id,
    companyNameEn: t.legalNameEn,
    companyNameAr: t.legalNameAr ?? t.legalNameEn,
    trn: t.trn,
    emirate: t.emirate ?? 'Dubai',
    industry: t.vatScheme,
    onboardingStatus: t.status,
    peppolId: t.peppolId ?? '',
    contactEmail: t.contactEmail,
    contactPhone: t.contactPhone ?? '',
    invoicesYtd: t._count?.invoices ?? 0,
    usersCount: t._count?.users ?? 0,
    createdAt: t.createdAt,
  };
}

export function adaptTenants(list: TenantDto[] | undefined): AdminTenantRow[] {
  return (list ?? []).map(adaptTenant);
}
